import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ORDER_TYPE, MARKET, PRIORITY, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/drift/common";

const NONE = "__none__";

export function NewOrderDialog({ open, onOpenChange, customerId, locationId, onCreated }) {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [montorar, setMontorar] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ customer_id: customerId || "", location_id: locationId || "", title: "", description: "", type: "service", market: "service_bustad", priority: "normal", assigned_to: "", scheduled_date: "", scheduled_time: "", estimated_hours: "" });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    if (!open) return;
    setF((p) => ({ ...p, customer_id: customerId || p.customer_id, location_id: locationId || "" }));
    api.get("/drift/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/drift/users?role=montor").then((r) => setMontorar(r.data)).catch(() => {});
  }, [open, customerId, locationId]);

  useEffect(() => {
    if (!f.customer_id) { setLocations([]); return; }
    api.get(`/drift/customers/${f.customer_id}`).then((r) => {
      setLocations(r.data.locations || []);
      const c = r.data;
      setF((p) => ({ ...p, market: p.market || (c.type === "industri" ? "industri" : c.type === "privat" ? "service_bustad" : "bygg_naering"), location_id: locationId || (r.data.locations?.length === 1 ? r.data.locations[0].id : p.location_id) }));
    }).catch(() => {});
  }, [f.customer_id, locationId]);

  const submit = async () => {
    if (!f.customer_id || !f.title.trim()) { toast.error("Vel kunde og skriv ein tittel"); return; }
    setBusy(true);
    try {
      const payload = { ...f, location_id: f.location_id || null, assigned_to: f.assigned_to || null, scheduled_date: f.scheduled_date || null, scheduled_time: f.scheduled_time || null, estimated_hours: f.estimated_hours ? Number(f.estimated_hours) : null };
      const r = await api.post("/drift/orders", payload);
      toast.success(`Oppdrag ${r.data.ref} oppretta`);
      onOpenChange(false);
      setF({ customer_id: customerId || "", location_id: locationId || "", title: "", description: "", type: "service", market: "service_bustad", priority: "normal", assigned_to: "", scheduled_date: "", scheduled_time: "", estimated_hours: "" });
      if (onCreated) onCreated(r.data); else navigate(`/drift/oppdrag/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="new-order-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Nytt oppdrag</DialogTitle>
          <DialogDescription>Opprett eit oppdrag manuelt. Tildel montør og dato no, eller planlegg seinare.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Kunde" className="sm:col-span-2">
            <Select value={f.customer_id} onValueChange={set("customer_id")} disabled={!!customerId}>
              <SelectTrigger data-testid="new-order-customer-select"><SelectValue placeholder="Vel kunde" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Anlegg / lokasjon" className="sm:col-span-2">
            <Select value={f.location_id || NONE} onValueChange={(v) => set("location_id")(v === NONE ? "" : v)} disabled={!!locationId || locations.length === 0}>
              <SelectTrigger data-testid="new-order-location-select"><SelectValue placeholder={locations.length ? "Vel anlegg" : "Kunden har ingen anlegg (vert oppretta)"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Standard / opprett ny</SelectItem>
                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} – {l.address}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Tittel" className="sm:col-span-2"><Input value={f.title} onChange={set("title")} placeholder="T.d. Feil på varmekablar bad" data-testid="new-order-title-input" /></Field>
          <Field label="Beskriving" className="sm:col-span-2"><Textarea rows={3} value={f.description} onChange={set("description")} data-testid="new-order-description-input" /></Field>
          <Field label="Type">
            <Select value={f.type} onValueChange={set("type")}><SelectTrigger data-testid="new-order-type-select"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(ORDER_TYPE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Marknad">
            <Select value={f.market} onValueChange={set("market")}><SelectTrigger data-testid="new-order-market-select"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(MARKET).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Prioritet">
            <Select value={f.priority} onValueChange={set("priority")}><SelectTrigger data-testid="new-order-priority-select"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Estimerte timar"><Input type="number" step="0.5" min="0" value={f.estimated_hours} onChange={set("estimated_hours")} data-testid="new-order-hours-input" /></Field>
          <Field label="Montør">
            <Select value={f.assigned_to || NONE} onValueChange={(v) => set("assigned_to")(v === NONE ? "" : v)}>
              <SelectTrigger data-testid="new-order-montor-select"><SelectValue placeholder="Ikkje tildelt" /></SelectTrigger>
              <SelectContent><SelectItem value={NONE}>Ikkje tildelt</SelectItem>{montorar.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Dato"><Input type="date" value={f.scheduled_date} min={todayISO()} onChange={set("scheduled_date")} data-testid="new-order-date-input" /></Field>
            <Field label="Tid"><Input type="time" value={f.scheduled_time} onChange={set("scheduled_time")} data-testid="new-order-time-input" /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={submit} disabled={busy} className="bg-brand text-white hover:bg-brand-dark" data-testid="new-order-submit-button">{busy ? "Opprettar …" : "Opprett oppdrag"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AssignDialog({ order, open, onOpenChange, onSaved, defaultDate }) {
  const [montorar, setMontorar] = useState([]);
  const [f, setF] = useState({ assigned_to: "", scheduled_date: "", scheduled_time: "08:00" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/drift/users?role=montor").then((r) => setMontorar(r.data)).catch(() => {});
    setF({ assigned_to: order?.assigned_to || "", scheduled_date: order?.scheduled_date || defaultDate || todayISO(), scheduled_time: order?.scheduled_time || "08:00" });
  }, [open, order, defaultDate]);

  const save = async () => {
    if (!f.assigned_to || !f.scheduled_date) { toast.error("Vel montør og dato"); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/drift/orders/${order.id}`, { ...f, status: order.status === "ny" ? "planlagt" : undefined });
      toast.success("Oppdraget er planlagt");
      onOpenChange(false);
      onSaved && onSaved(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Planlegg oppdrag</DialogTitle>
          <DialogDescription>{order?.ref} · {order?.title}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <Field label="Montør">
            <Select value={f.assigned_to} onValueChange={(v) => setF((p) => ({ ...p, assigned_to: v }))}>
              <SelectTrigger data-testid="assign-montor-select"><SelectValue placeholder="Vel montør" /></SelectTrigger>
              <SelectContent>{montorar.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} – {m.title}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Dato"><Input type="date" value={f.scheduled_date} onChange={(e) => setF((p) => ({ ...p, scheduled_date: e.target.value }))} data-testid="assign-date-input" /></Field>
            <Field label="Tid"><Input type="time" value={f.scheduled_time} onChange={(e) => setF((p) => ({ ...p, scheduled_time: e.target.value }))} data-testid="assign-time-input" /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={save} disabled={busy} className="bg-brand text-white hover:bg-brand-dark" data-testid="assign-save-button">{busy ? "Lagrar …" : "Lagre"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function useMontorar() {
  const [montorar, setMontorar] = useState([]);
  useEffect(() => { api.get("/drift/users?role=montor").then((r) => setMontorar(r.data)).catch(() => {}); }, []);
  return montorar;
}

export function useUsers() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/drift/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  return useMemo(() => users, [users]);
}
